import PieChartWidget from './pieChartWidget'
import { checkIfAllEmpty } from './utils'
import {
  ACTION_FETCH_DSR_TYPES_SUMMARY,
  DsrRequestsCount,
  DsrTypeCount,
  fetchDsrTypesSummary
} from '../privacyOpsOverviewSlice'
import { getCreatedDateUrlFilter } from '../../dsr/utils'
import { RootState } from '../../../rootReducer'
import { getShowLoader } from '../../../reducers/requestReducer'
import { getUpdatedUrl } from '../../../utils/urlUtil'
import {
  DSR_REQUEST_STATUS_FILTER_TYPES,
  FILTER_CREATED_AT,
  FILTER_REQUEST_TYPE,
  FILTER_STATUS_KEY,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_DSR,
  URL_DSR_REQUEST_MANAGEMENT
} from '../../../constants'
import { useIntl } from 'react-intl'
import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Typography } from '@lightbeamai/design-system'

const MAP_ATTRIBUTE_TOP_CLASS_TO_COLORS = [
  '#2899E0',
  '#723DFD',
  '#00A198',
  '#2E55E5',
  '#F4BF20',
  '#737D93'
]

interface IProps {
  dsrTypesSummary: DsrTypeCount[]
  dsrRequestsCount?: DsrRequestsCount
  timePeriod?: string
  fetchDsrTypesSummary: (timePeriod: string) => void
  showLoader: boolean
}

const DSRTypesWidget = ({
  dsrTypesSummary,
  dsrRequestsCount,
  timePeriod = '',
  fetchDsrTypesSummary,
  showLoader
}: IProps) => {
  const intl = useIntl()

  const createdDateUrlFilter = useMemo(() => getCreatedDateUrlFilter(timePeriod), [timePeriod])

  useEffect(() => {
    fetchDsrTypesSummary(timePeriod)
  }, [fetchDsrTypesSummary, timePeriod])

  const formattedData = useMemo(
    () =>
      dsrTypesSummary.map((item, index) => ({
        key: intl.formatMessage({ id: `dsr.requestType.${item.type.toLowerCase()}` }),
        value: item.count,
        color: MAP_ATTRIBUTE_TOP_CLASS_TO_COLORS[index],
        link: getUpdatedUrl({
          pathName: `${URL_DSR}${URL_DSR_REQUEST_MANAGEMENT}`,
          pageName: URL_DSR_REQUEST_MANAGEMENT,
          pageQueryParams: {
            [FILTER_REQUEST_TYPE]: item.type,
            [FILTER_CREATED_AT]: createdDateUrlFilter
          }
        })
      })),
    [dsrTypesSummary]
  )

  return (
    <PieChartWidget
      title={intl.formatMessage({ id: 'po.dashboard.dsrTypes.widget.title' })}
      data={checkIfAllEmpty(formattedData)}
      legendPosition="right"
      showLoader={showLoader}
      viewAllBtnText={intl.formatMessage({ id: 'po.dashboard.dsrTypes.widget.viewAllBtn' })}
      viewAllLink={`${URL_DSR}${URL_DSR_REQUEST_MANAGEMENT}`}
      showSummary={true}
      Summary={
        <div className="xs-mr-32 xs-mt-16">
          <Link
            to={getUpdatedUrl({
              pathName: `${URL_DSR}${URL_DSR_REQUEST_MANAGEMENT}`,
              pageName: URL_DSR_REQUEST_MANAGEMENT,
              pageQueryParams: {
                [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
                [FILTER_CREATED_AT]: createdDateUrlFilter
              },
              queryParams: {
                [FILTER_STATUS_KEY]: DSR_REQUEST_STATUS_FILTER_TYPES.inProgress
              }
            })}
            className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
          >
            <Typography variant={'basePlus6'} type={'strong'}>
              {dsrRequestsCount?.inProgress || 0}
            </Typography>
            <Typography variant={'baseMinus2'} type={'medium'}>
              {intl.formatMessage({ id: 'status.inProgress' })}
            </Typography>
          </Link>
          <Link
            to={getUpdatedUrl({
              pathName: `${URL_DSR}${URL_DSR_REQUEST_MANAGEMENT}`,
              pageName: URL_DSR_REQUEST_MANAGEMENT,
              pageQueryParams: {
                [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
                [FILTER_CREATED_AT]: createdDateUrlFilter
              },
              queryParams: {
                [FILTER_STATUS_KEY]: DSR_REQUEST_STATUS_FILTER_TYPES.unverified
              }
            })}
            className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
          >
            <Typography variant={'basePlus6'} type={'strong'}>
              {dsrRequestsCount?.awaitingApproval || 0}
            </Typography>
            <Typography variant={'baseMinus2'} type={'medium'}>
              {intl.formatMessage({ id: 'status.unverified' })}
            </Typography>
          </Link>
          <Link
            to={getUpdatedUrl({
              pathName: `${URL_DSR}${URL_DSR_REQUEST_MANAGEMENT}`,
              pageName: URL_DSR_REQUEST_MANAGEMENT,
              pageQueryParams: {
                [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
                [FILTER_CREATED_AT]: createdDateUrlFilter
              },
              queryParams: {
                [FILTER_STATUS_KEY]: DSR_REQUEST_STATUS_FILTER_TYPES.closed
              }
            })}
            className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
          >
            <Typography variant={'basePlus6'} type={'strong'}>
              {dsrRequestsCount?.closed || 0}
            </Typography>
            <Typography variant={'baseMinus2'} type={'medium'}>
              {intl.formatMessage({ id: 'status.closed' })}
            </Typography>
          </Link>
        </div>
      }
      totalText={intl.formatMessage({ id: 'dsr.requestManagement.totalRequests' })}
    />
  )
}

const mapStateToProps = (state: RootState) => ({
  dsrTypesSummary: state.privacyOpsOverview.dsrTypesSummary,
  dsrRequestsCount: state.privacyOpsOverview.dsrRequestsCount,
  showLoader: getShowLoader(ACTION_FETCH_DSR_TYPES_SUMMARY),
  timePeriod: state.customDashboards.filterProps?.timePeriod
})

const mapDispatchToProps = {
  fetchDsrTypesSummary
}

export default connect(mapStateToProps, mapDispatchToProps)(DSRTypesWidget)
